// "Preguntale a tu obra": chat (texto + micrófono) dentro del Calendario de obra. El cliente pregunta en
// castellano ("¿a qué hora entró la primera persona el lunes?") y Claude contesta con los datos del mes
// cargado y las fotos que lo respaldan.
// Modo conversación (como el modo voz de la app de Claude): tocás el micrófono, hablás, ves el texto en vivo,
// se manda solo cuando te callás, la respuesta se lee en voz alta mientras aparecen las fotos y vuelve a escuchar.
// Toda la parte de micrófono y voz está en vozObra.js.
// Backend: POST /api/obra-chat (lisual-admin-backend). En local: REACT_APP_WIDGET_API_BASE (servidor de prueba 8094).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { photoUrl, fmtHM, fmtKey } from './iaData';
import { buildDigest, nearestPhoto } from './chatDigest';
import { T } from './ui';
import { SR, TTS, PUEDE_GRABAR, MENSAJES, crearMicrofono, hablar, callar, listarMics } from './vozObra';

const ENDPOINT = process.env.REACT_APP_OBRA_CHAT_URL
  || (process.env.NODE_ENV === 'development' && process.env.REACT_APP_WIDGET_API_BASE
    ? `${process.env.REACT_APP_WIDGET_API_BASE}/api/obra-chat`
    : `${process.env.REACT_APP_BACKEND_URL || ''}/api/obra-chat`);
const FASES = { abriendo: 'Abriendo el micrófono… (si el navegador pregunta, tocá Permitir)', escuchando: 'Escuchando… hablá cuando quieras', hablando: 'Te escucho…', procesando: 'Pasando tu voz a texto…', pensando: 'Buscando en los datos de la obra…', respondiendo: 'Respondiendo… (tocá el micrófono para interrumpir)', quieto: 'Tocá el micrófono para hablar' };
const authHeader = () => { const t = localStorage.getItem('authLisualToken'); return t ? { Authorization: `Bearer ${t}` } : {}; };
const PUEDE_DICTAR = !!SR || PUEDE_GRABAR;

const SUGERENCIAS = [
  '¿A qué hora entró la primera persona hoy?',
  '¿Qué día hubo más gente esta semana?',
  '¿Cuántas horas se trabajó la semana pasada?',
  '¿Llovió esta semana? ¿Se trabajó igual?',
  '¿Qué porcentaje usaba casco ayer?',
  'Mostrame la foto de las 10 de hoy',
];

const store = {
  get: (k, d) => { try { const v = sessionStorage.getItem(k); return v ? JSON.parse(v) : d; } catch (e) { return d; } },
  set: (k, v) => { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* sin storage */ } },
};
const pref = {
  get: (k) => { try { return localStorage.getItem(k) === '1'; } catch (e) { return false; } },
  set: (k, v) => { try { localStorage.setItem(k, v ? '1' : '0'); } catch (e) { /* */ } },
};

function Thumb({ photo, caption, onOpen }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    if (photo) photoUrl(photo).then((u) => alive && setUrl(u)).catch(() => {});
    return () => { alive = false; };
  }, [photo]);
  return (
    <button type="button" onClick={() => photo && onOpen(photo, url)} disabled={!photo}
      style={{ padding: 0, border: `1px solid ${T.line}`, borderRadius: 10, overflow: 'hidden', background: '#fff', cursor: photo ? 'zoom-in' : 'default', textAlign: 'left', font: 'inherit', color: 'inherit', minWidth: 0 }}>
      <div style={{ aspectRatio: '16 / 9', background: '#e5e7eb' }}>
        {url && <img src={url} alt={caption} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
        {!photo && <div style={{ height: '100%', display: 'grid', placeItems: 'center', fontSize: 11, color: T.muted, padding: 6, textAlign: 'center' }}>No hay foto de ese momento</div>}
      </div>
      <div style={{ fontSize: 11, padding: '4px 6px', color: T.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {caption}{photo ? ` · ${fmtKey(photo.dayKey || '').slice(0, 5)} ${fmtHM(photo.ms)}` : ''}
      </div>
    </button>
  );
}

export default function ChatObra({ cam, customerId, feedId, name, photos, cfg, wxDays, fromKey, toKey, today, loading, onClose, entregas }) {
  const key = 'obraChat:' + (feedId || cam);
  const [msgs, setMsgs] = useState(() => store.get(key, []));
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [conv, setConv] = useState(false); // modo conversación (manos libres)
  const [fase, setFase] = useState('quieto'); // quieto | abriendo | escuchando | hablando | procesando | pensando | respondiendo
  const [interim, setInterim] = useState('');
  const [nivel, setNivel] = useState(0);
  const [micError, setMicError] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [micUsado, setMicUsado] = useState(null);
  const [mics, setMics] = useState([]);
  const [voz, setVoz] = useState(() => pref.get('obraChat:voz'));
  const [zoom, setZoom] = useState(null);
  const micRef = useRef(null);
  const convRef = useRef(false);
  const vozRef = useRef(voz);
  const sendRef = useRef(null);
  const finalRef = useRef(null);
  const silencios = useRef(0);
  vozRef.current = voz;
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width:640px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width:640px)');
    const f = () => setPhone(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', f) : mq.addListener(f);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', f) : mq.removeListener(f));
  }, []);
  // El globo "¿Necesitás ayuda?" queda arriba de todo: mientras el chat está abierto se esconde y al cerrar vuelve.
  useEffect(() => {
    const host = document.getElementById('lisual-widget-host');
    if (!host) return undefined;
    const prev = host.style.display;
    host.style.display = 'none';
    return () => { host.style.display = prev; };
  }, []);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { setMsgs(store.get(key, [])); }, [key]);
  useEffect(() => { store.set(key, msgs.slice(-30)); }, [key, msgs]);
  useEffect(() => { pref.set('obraChat:voz', voz); if (!voz) callar(); }, [voz]);
  useEffect(() => { endRef.current && endRef.current.scrollIntoView({ block: 'end', behavior: 'smooth' }); }, [msgs, busy, interim]);
  useEffect(() => () => { convRef.current = false; callar(); if (micRef.current) micRef.current.cerrar(); }, []);
  useEffect(() => {
    const f = (e) => { if (e.key === 'Escape') { if (zoom) setZoom(null); else if (convRef.current) pararConv(); else onClose(); } };
    window.addEventListener('keydown', f);
    return () => window.removeEventListener('keydown', f);
  }, [zoom, onClose]);

  const digest = useMemo(() => (loading || !photos.length ? null : buildDigest({ photos, cfg, wxDays, fromKey, toKey, name, today, entregas })), [loading, photos, cfg, wxDays, fromKey, toKey, name, today, entregas]);

  // Para revisar en local qué datos recibe el asistente: window.__obraChatDigest
  useEffect(() => { if (process.env.NODE_ENV === 'development') window.__obraChatDigest = digest; }, [digest]);

  const send = async (q) => {
    const question = String(q || '').trim();
    if (!question || busy) return;
    if (!digest) { setMsgs((m) => [...m, { role: 'user', text: question }, { role: 'assistant', text: 'Esperá que termine de cargar los datos del mes y preguntame de nuevo.', error: true }]); return; }
    const history = msgs.filter((m) => !m.error).slice(-8).map((m) => ({ role: m.role, text: m.text }));
    setMsgs((m) => [...m, { role: 'user', text: question }]);
    setText('');
    setBusy(true);
    setFase('pensando');
    let respuesta = null;
    try {
      const r = await axios.post(ENDPOINT, { feedId, customerId, question, history, digest }, { headers: authHeader(), timeout: 45000 });
      const a = r.data || {};
      respuesta = a.answer || 'No encontré una respuesta.';
      setMsgs((m) => [...m, { role: 'assistant', text: respuesta, photos: a.photos || [] }]);
    } catch (e) {
      respuesta = (e.response && e.response.data && e.response.data.message) || (e.code === 'ECONNABORTED' ? 'Tardó demasiado en responder. Probá de nuevo.' : 'No pude conectarme con el asistente. Probá de nuevo en un momento.');
      setMsgs((m) => [...m, { role: 'assistant', text: respuesta, error: true }]);
    } finally {
      setBusy(false);
    }
    // Texto y fotos ya están en pantalla; ahora se lee y, en modo conversación, se vuelve a escuchar.
    if (vozRef.current && respuesta) {
      setFase('respondiendo');
      hablar(respuesta, () => { if (convRef.current) escuchar(); else setFase('quieto'); });
    } else if (convRef.current) escuchar();
    else setFase('quieto');
  };
  sendRef.current = send;

  // ---------------------------------------------------------------------------
  // Micrófono y modo conversación
  // ---------------------------------------------------------------------------
  const transcribir = async (blob) => {
    const r = await axios.post(`${ENDPOINT}/stt?feedId=${encodeURIComponent(feedId || '')}`, blob, { headers: { ...authHeader(), 'Content-Type': blob.type || 'audio/webm' }, timeout: 30000 });
    return String((r.data && r.data.text) || '').trim();
  };
  const mic = () => {
    if (!micRef.current) {
      micRef.current = crearMicrofono({
        onState: (e) => setFase((f) => (e === 'quieto' && (f === 'pensando' || f === 'respondiendo') ? f : e)),
        onInterim: setInterim,
        onFinal: (t) => finalRef.current && finalRef.current(t),
        onError: (k, det) => {
          console.warn('[obra-voz] error', k, det || '');
          setMicError(MENSAJES[k] || MENSAJES.sttFallo);
          if (k === 'mudo' || k === 'sinMic') listarMics().then(setMics);
          if (k !== 'red' && k !== 'noOi') { convRef.current = false; setConv(false); }
        },
        onLevel: setNivel,
        onAviso: (t) => { console.info('[obra-voz]', t); setAviso(t); },
        onMic: (m) => { setMicUsado(m); listarMics().then(setMics); },
      }, transcribir);
    }
    return micRef.current;
  };
  finalRef.current = (t) => {
    setInterim('');
    if (t) { silencios.current = 0; setMicError(null); sendRef.current(t); return; }
    if (!convRef.current) return;
    // No dijo nada: se vuelve a escuchar un par de veces y después se corta la conversación.
    silencios.current += 1;
    if (silencios.current <= 2) { setMicError(null); escuchar(); } else { setMicError(MENSAJES.noOi); pararConv(); }
  };
  function escuchar() {
    try {
      callar();
      Promise.resolve(mic().escuchar()).catch((e) => { console.warn('[obra-voz] escuchar', e); setMicError(MENSAJES.sinSoporte); setFase('quieto'); });
    } catch (e) { console.warn('[obra-voz] escuchar', e); setMicError(MENSAJES.sinSoporte); setFase('quieto'); }
  }
  function pararConv() {
    convRef.current = false;
    setConv(false);
    callar();
    if (micRef.current) micRef.current.cerrar();
    setInterim('');
    setFase('quieto');
  }
  const tocarMic = () => {
    console.info('[obra-voz] tocar micrófono', { conv: convRef.current, fase, SR: !!SR, grabar: PUEDE_GRABAR });
    setMicError(null);
    setAviso(null);
    if (!PUEDE_DICTAR) { setMicError(MENSAJES.sinSoporte); return; }
    if (!convRef.current) {
      convRef.current = true; setConv(true); silencios.current = 0;
      if (TTS && !vozRef.current) setVoz(true);
      vozRef.current = true;
      if (!busy) escuchar();
      return;
    }
    if (fase === 'respondiendo') { escuchar(); return; } // interrumpe la voz y escucha
    if (fase === 'escuchando' || fase === 'hablando') { mic().terminar(); return; } // manda lo que ya dijo
    if (fase === 'quieto') escuchar();
  };

  // Interrumpir hablando: si mientras lee la respuesta el micrófono capta voz fuerte un rato, se calla y escucha.
  useEffect(() => {
    if (fase !== 'respondiendo' || !conv || !micRef.current || !micRef.current.abierto) return undefined;
    let fuerte = 0;
    const t0 = Date.now();
    const id = setInterval(() => {
      if (Date.now() - t0 < 700) return; // el arranque de la voz no cuenta
      const v = Math.min(1, micRef.current.nivel() * 8);
      fuerte = v > 0.65 ? fuerte + 1 : 0;
      if (fuerte >= 6) { console.info('[obra-voz] interrupción por voz'); clearInterval(id); escuchar(); }
    }, 60);
    return () => clearInterval(id);
  }, [fase, conv]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolved = (m) => (m.photos || []).map((f) => {
    const p = nearestPhoto(photos, f.day, f.time);
    return { ...f, photo: p ? { ...p, dayKey: f.day } : null };
  });

  // Centrado en pantalla (el globo de ayuda vive abajo a la derecha); en el celular, pantalla completa.
  const panel = phone
    ? { inset: 0, borderRadius: 0 }
    : { left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: 'min(720px, calc(100vw - 32px))', height: 'min(80vh, 760px)', borderRadius: 20, boxShadow: '0 24px 70px rgba(0,0,0,.28)' };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)', zIndex: 1300 }}>
      <style>{`
        @keyframes obraMicPulse{0%{box-shadow:0 0 0 0 rgba(239,68,68,.55)}70%{box-shadow:0 0 0 16px rgba(239,68,68,0)}100%{box-shadow:0 0 0 0 rgba(239,68,68,0)}}
        @keyframes obraDots{0%,80%,100%{opacity:.25}40%{opacity:1}}
        .obra-chip{border:1px solid ${T.line};background:#fff;border-radius:999px;padding:6px 11px;font:inherit;font-size:12.5px;color:${T.text};cursor:pointer;text-align:left}
        .obra-chip:hover{border-color:${T.blue};color:${T.blue}}
      `}</style>
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Preguntale a tu obra"
        style={{ position: 'absolute', background: T.bg, display: 'flex', flexDirection: 'column', color: T.text, overflow: 'hidden', ...panel }}>
        <div style={{ background: '#fff', borderBottom: `1px solid ${T.line}`, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: '#eff6ff', display: 'grid', placeItems: 'center', fontSize: 18, flex: 'none' }}>💬</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Preguntale a tu obra</div>
            <div style={{ fontSize: 11.5, color: T.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {loading ? 'Cargando los datos del mes…' : `Datos del ${fmtKey(fromKey).slice(0, 5)} al ${fmtKey(toKey).slice(0, 5)} · ${name || cam}`}
            </div>
          </div>
          {TTS && (
            <button type="button" onClick={() => setVoz(!voz)} title={voz ? 'Dejar de leer las respuestas en voz alta' : 'Leer las respuestas en voz alta'} aria-pressed={voz}
              style={{ border: `1px solid ${voz ? T.blue : T.line}`, background: voz ? '#eff6ff' : '#fff', color: voz ? T.blue : T.muted, borderRadius: 10, padding: '6px 9px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              {voz ? '🔊 Voz' : '🔈 Voz'}
            </button>
          )}
          {msgs.length > 0 && <button type="button" onClick={() => { setMsgs([]); callar(); }} title="Empezar de nuevo" style={{ border: `1px solid ${T.line}`, background: '#fff', borderRadius: 10, padding: '6px 9px', cursor: 'pointer', fontSize: 12, color: T.muted }}>Limpiar</button>}
          <button type="button" onClick={onClose} title="Cerrar (Esc)" style={{ border: `1px solid ${T.line}`, background: '#fff', borderRadius: 10, padding: '6px 10px', cursor: 'pointer', fontSize: 13 }}>✕</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {!msgs.length && (
            <div>
              <div style={{ background: '#fff', border: `1px solid ${T.line}`, borderRadius: 14, padding: 12, fontSize: 14, lineHeight: 1.5 }}>
                Hola 👋 Preguntame lo que quieras saber de la obra: a qué hora llegó la gente, qué día hubo más movimiento, si se trabajó con lluvia o el uso de casco. Te contesto con los datos de la cámara y las fotos.
                {PUEDE_DICTAR ? ' Tocá el micrófono y hablá como en una charla: te contesto en voz alta y vuelvo a escuchar.' : ''}
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '.05em', margin: '14px 0 8px' }}>Probá con</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {SUGERENCIAS.map((s) => <button key={s} type="button" className="obra-chip" onClick={() => send(s)} disabled={busy}>{s}</button>)}
              </div>
            </div>
          )}
          {msgs.map((m, i) => (
            m.role === 'user' ? (
              <div key={i} style={{ alignSelf: 'flex-end', maxWidth: '85%', background: T.blue, color: '#fff', borderRadius: '14px 14px 4px 14px', padding: '8px 12px', fontSize: 14, lineHeight: 1.45 }}>{m.text}</div>
            ) : (
              <div key={i} style={{ alignSelf: 'flex-start', maxWidth: phone ? '94%' : '86%', width: m.photos && m.photos.length ? (phone ? '94%' : '86%') : undefined }}>
                <div style={{ background: m.error ? '#fef2f2' : '#fff', border: `1px solid ${m.error ? '#fecaca' : T.line}`, color: m.error ? '#991b1b' : T.text, borderRadius: '14px 14px 14px 4px', padding: '9px 12px', fontSize: 14, lineHeight: 1.5 }}>
                  {m.text}
                  {!m.error && TTS && <button type="button" onClick={() => hablar(m.text)} title="Escuchar" style={{ marginLeft: 6, border: 0, background: 'none', cursor: 'pointer', fontSize: 13, color: T.muted, padding: 0 }}>🔈</button>}
                </div>
                {m.photos && m.photos.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(phone ? 2 : 3, m.photos.length)}, minmax(0, 1fr))`, gap: 6, marginTop: 6 }}>
                    {resolved(m).map((f, j) => <Thumb key={j} photo={f.photo} caption={f.caption} onOpen={(p, url) => setZoom({ p, url, caption: f.caption })} />)}
                  </div>
                )}
              </div>
            )
          ))}
          {interim && (
            <div style={{ alignSelf: 'flex-end', maxWidth: '85%', background: '#eff6ff', color: T.blue, border: `1px dashed ${T.blue}`, borderRadius: '14px 14px 4px 14px', padding: '8px 12px', fontSize: 14, lineHeight: 1.45, fontStyle: 'italic' }} aria-live="polite">{interim}</div>
          )}
          {busy && (
            <div style={{ alignSelf: 'flex-start', background: '#fff', border: `1px solid ${T.line}`, borderRadius: 14, padding: '9px 14px', fontSize: 18, letterSpacing: 2, color: T.muted }} aria-label="Pensando">
              {[0, 1, 2].map((d) => <span key={d} style={{ animation: `obraDots 1.2s ${d * 0.2}s infinite` }}>•</span>)}
            </div>
          )}
          <div ref={endRef} />
        </div>

        {aviso && !micError && (
          <div style={{ background: '#fffbeb', borderTop: '1px solid #fde68a', padding: '6px 14px', fontSize: 12.5, color: '#92400e', display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ flex: 1 }}>{aviso}</span>
            <button type="button" onClick={() => setAviso(null)} title="Cerrar aviso" style={{ border: 0, background: 'none', cursor: 'pointer', color: '#92400e' }}>✕</button>
          </div>
        )}
        {(conv || micError) && (
          <div style={{ background: micError ? '#fef2f2' : '#f8fafc', borderTop: `1px solid ${micError ? '#fecaca' : T.line}`, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: micError ? '#991b1b' : T.text }} role="status" aria-live="polite" data-obra-estado>
            {micError ? <span style={{ flex: 1 }}>{micError}</span> : (
              <>
                <span style={{ display: 'inline-flex', alignItems: 'flex-end', gap: 2, height: 18 }} aria-hidden="true">
                  {[0.5, 0.8, 1, 0.8, 0.5].map((k, i) => <span key={i} style={{ width: 4, borderRadius: 2, background: fase === 'escuchando' || fase === 'hablando' ? T.red : '#cbd5e1', height: `${Math.max(3, Math.round(18 * Math.min(1, nivel * k * 1.4)))}px`, transition: 'height 60ms' }} />)}
                </span>
                <span style={{ flex: 1, fontWeight: 600 }}>{FASES[fase] || 'Modo conversación'}</span>
              </>
            )}
            {mics.length > 1 && (
              <select value={(mics.find((m) => micUsado && m.id === micUsado.id) || {}).id || ''} title="Micrófono"
                onChange={(e) => { const id = e.target.value; setAviso(null); setMicError(null); const enCharla = convRef.current; mic().cambiarMic(id); if (enCharla) setTimeout(() => escuchar(), 150); }}
                style={{ maxWidth: 170, fontSize: 12, border: `1px solid ${T.line}`, borderRadius: 8, padding: '4px 6px', background: '#fff', color: T.text, flex: 'none' }}>
                {!micUsado && <option value="">Micrófono…</option>}
                {mics.map((m) => <option key={m.id} value={m.id}>🎙 {m.nombre.replace(/^Micrófono\s*/i, '').replace(/\s*\([0-9a-f]{4}:[0-9a-f]{4}\)$/i, '') || 'Micrófono'}</option>)}
              </select>
            )}
            {conv && <button type="button" onClick={pararConv} style={{ border: `1px solid ${T.line}`, background: '#fff', borderRadius: 10, padding: '5px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: T.text, flex: 'none' }}>Terminar charla</button>}
            {micError && !conv && <button type="button" onClick={() => setMicError(null)} title="Cerrar aviso" style={{ border: 0, background: 'none', cursor: 'pointer', color: '#991b1b', fontSize: 14 }}>✕</button>}
          </div>
        )}
        <form onSubmit={(e) => { e.preventDefault(); send(text); }} style={{ background: '#fff', borderTop: `1px solid ${T.line}`, padding: phone ? '10px 12px max(10px, env(safe-area-inset-bottom))' : '12px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <button type="button" onClick={tocarMic} data-obra-mic={fase} aria-pressed={conv}
            title={!conv ? 'Hablar (modo conversación)' : fase === 'respondiendo' ? 'Interrumpir y hablar' : fase === 'escuchando' || fase === 'hablando' ? 'Listo, mandar lo que dije' : 'Hablar'}
            style={{ width: 56, height: 56, borderRadius: 999, border: 0, flex: 'none', cursor: 'pointer', background: fase === 'escuchando' || fase === 'hablando' ? T.red : conv ? '#1d4ed8' : PUEDE_DICTAR ? T.blue : '#9ca3af', color: '#fff', display: 'grid', placeItems: 'center', animation: fase === 'escuchando' || fase === 'hablando' ? 'obraMicPulse 1.4s infinite' : fase === 'abriendo' ? 'obraMicPulse 0.8s infinite' : 'none', transform: `scale(${fase === 'hablando' ? 1 + Math.min(0.12, nivel * 0.25) : 1})`, transition: 'transform 80ms, background .2s' }}>
            {fase === 'respondiendo' ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>
            ) : (
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="9" y="2" width="6" height="12" rx="3" /><path d="M5 10a7 7 0 0 0 14 0" /><line x1="12" y1="17" x2="12" y2="22" />
              </svg>
            )}
          </button>
          <input ref={inputRef} value={text} onChange={(e) => setText(e.target.value)} disabled={busy}
            placeholder={fase === 'escuchando' || fase === 'hablando' ? 'Escuchando…' : PUEDE_DICTAR ? 'Hablá o escribí tu pregunta' : 'Escribí tu pregunta (tu navegador no permite dictar)'}
            style={{ flex: 1, minWidth: 0, border: `1px solid ${fase === 'escuchando' || fase === 'hablando' ? T.red : T.line}`, borderRadius: 12, padding: '11px 12px', fontSize: 15, outline: 'none', background: '#fff', color: T.text, colorScheme: 'light' }} />
          <button type="submit" disabled={busy || !text.trim()} title="Enviar"
            style={{ height: 44, padding: '0 14px', borderRadius: 12, border: 0, background: busy || !text.trim() ? '#c7d2fe' : T.blue, color: '#fff', fontWeight: 700, cursor: busy || !text.trim() ? 'not-allowed' : 'pointer', flex: 'none' }}>
            Enviar
          </button>
        </form>
      </div>

      {zoom && (
        <div onClick={(e) => { e.stopPropagation(); setZoom(null); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', zIndex: 1400, display: 'grid', placeItems: 'center', padding: 16, cursor: 'zoom-out' }}>
          <div style={{ maxWidth: 'min(1200px, 100%)', width: '100%' }}>
            {zoom.url ? <img src={zoom.url} alt={zoom.caption} style={{ width: '100%', borderRadius: 10, display: 'block' }} /> : <div style={{ color: '#fff' }}>Cargando foto…</div>}
            <div style={{ color: '#fff', fontSize: 14, marginTop: 8 }}>{zoom.caption} · {fmtKey(zoom.p.dayKey)} {fmtHM(zoom.p.ms)} h</div>
          </div>
        </div>
      )}
    </div>
  );
}
